import React, { createContext, useContext, useEffect, useState } from 'react';
import { AgentId, setAgentTheme, setTheme } from '../lib/utils';

type Theme = 'light' | 'dark';

/* Shared with the pre-paint inline script in index.html. Keep in sync. */
const THEME_STORAGE_KEY = 'cc-theme';

const readStoredTheme = (): Theme => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  } catch {
    return 'dark';
  }
};

interface DesignSystemContextValue {
  theme: Theme;
  currentAgent?: AgentId;
  setTheme: (theme: Theme) => void;
  setAgent: (agentId?: AgentId) => void;
}

const DesignSystemContext = createContext<DesignSystemContextValue | null>(null);

export const DesignSystemProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(readStoredTheme);
  const [currentAgent, setCurrentAgent] = useState<AgentId>();

  useEffect(() => {
    setTheme(currentTheme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
    } catch {
      /* localStorage unavailable (private mode); theme still applies for this session */
    }
  }, [currentTheme]);

  useEffect(() => {
    setAgentTheme(currentAgent);
  }, [currentAgent]);

  const contextValue: DesignSystemContextValue = {
    theme: currentTheme,
    currentAgent,
    setTheme: setCurrentTheme,
    setAgent: setCurrentAgent
  };

  return (
    <DesignSystemContext.Provider value={contextValue}>
      {children}
    </DesignSystemContext.Provider>
  );
};

export const useDesignSystem = () => {
  const context = useContext(DesignSystemContext);
  if (!context) {
    throw new Error('useDesignSystem must be used within DesignSystemProvider');
  }
  return context;
};
